import React, { useState, useEffect } from "react";
import { Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FiPlus, FiRefreshCw } from "react-icons/fi";
import PageLayout from "../../layouts/PageLayout";

export default function SellerEditProductPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const passedItem = location.state?.item || {};

    const [loadingInit, setLoadingInit] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingGallery, setUploadingGallery] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Initial Data States
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [selectedParentCategory, setSelectedParentCategory] = useState("");

    // Form State mapped to updateFreshProduct backend validation rules
    const [formData, setFormData] = useState({
        product_id: passedItem?.id || "",
        category_id: passedItem?.category?.id || passedItem?.category_id || "",
        brand_id: passedItem?.brand?.id || passedItem?.brand_id || "",
        title: passedItem?.title || "",
        product_qty: passedItem?.product_qty ?? passedItem?.qty ?? 0,
        product_condition: passedItem?.product_condition || "new",
        buying_price: passedItem?.buying_price ?? "",
        sell_price_dollar: passedItem?.sell_price_dollar ?? "",
        shipping_charges_dollar: passedItem?.shipping_charges_dollar ?? "",
        short_buying_price: passedItem?.short_buying_price ?? "",
        buying_price: passedItem?.buying_price ?? "",
        short_description: passedItem?.short_description || "",
        description: passedItem?.description || "",
    });

    const [mainImage, setMainImage] = useState(null);
    const [mainImagePreview, setMainImagePreview] = useState(
        passedItem.card_img ? `${process.env.REACT_APP_IMG_URL}/${passedItem.card_img}` : null
    );

    const [imagesList, setImagesList] = useState(passedItem?.images || []);

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser")}`,
    });

    // Fetch Initial Data and Set Pre-selected Categories
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/store-seller/initial-data`,
                    { headers: getAuthHeaders() }
                );

                const fetchedBrands = res.data?.brand || [];
                const fetchedCategories = res.data?.product_categories || [];

                setBrands(fetchedBrands);
                setCategories(fetchedCategories);

                const currentCatId = passedItem?.category?.id || passedItem?.category_id;
                if (currentCatId && fetchedCategories.length) {
                    for (let parentCat of fetchedCategories) {
                        const childFound = parentCat.child_categories?.find(
                            (sub) => String(sub.id) === String(currentCatId)
                        );
                        if (childFound) {
                            setSelectedParentCategory(parentCat.id);
                            setSubCategories(parentCat.child_categories);
                            break;
                        }
                    }
                }
            } catch (err) {
                console.error("Fetch initial data error:", err);
                setError("Failed to load categories and brands.");
            } finally {
                setLoadingInit(false);
            }
        };

        fetchInitialData();
    }, [passedItem]);

    const handleParentCategoryChange = (e) => {
        const parentId = e.target.value;
        setSelectedParentCategory(parentId);
        setFormData((prev) => ({ ...prev, category_id: "" }));

        const selectedCat = categories.find((c) => String(c.id) === String(parentId));
        if (selectedCat && selectedCat.child_categories) {
            setSubCategories(selectedCat.child_categories);
        } else {
            setSubCategories([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleQuillChange = (value) => {
        setFormData((prev) => ({ ...prev, description: value }));
    };

    const handleMainImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setMainImage(file);
        setMainImagePreview(URL.createObjectURL(file));
    };

    const handleMultiImageUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length || !formData.product_id) return;

        setUploadingGallery(true);
        setError(null);

        try {
            for (let file of files) {
                const imgData = new FormData();
                imgData.append("model", "products");
                imgData.append("id", formData.product_id);
                imgData.append("image", file);

                // Endpoint updated to Route::put('/upload-image')
                const res = await axios.put(
                    `${process.env.REACT_APP_API_URL}/store-seller/upload-image`,
                    imgData,
                    {
                        headers: {
                            ...getAuthHeaders(),
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

                if (res.data?.data?.images) {
                    setImagesList(res.data.data.images);
                }
            }
            setSuccess("Gallery images uploaded successfully!");
        } catch (err) {
            console.error("Gallery upload error:", err);
            setError(err.response?.data?.message || "Failed to upload gallery images.");
        } finally {
            setUploadingGallery(false);
        }
    };

    const handleSingleImageReplace = async (imageId, file) => {
        if (!file || !imageId) return;

        setUploadingGallery(true);
        setError(null);

        try {
            const imgData = new FormData();
            imgData.append("model", "product_images");
            imgData.append("id", imageId);
            imgData.append("image", file);

            // Endpoint updated to Route::put('/upload-image')
            const res = await axios.put(
                `${process.env.REACT_APP_API_URL}/store-seller/upload-image`,
                imgData,
                {
                    headers: {
                        ...getAuthHeaders(),
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (res.data?.data?.images) {
                setImagesList(res.data.data.images);
            }
            setSuccess("Image updated successfully!");
        } catch (err) {
            console.error("Image replace error:", err);
            setError(err.response?.data?.message || "Failed to replace image.");
        } finally {
            setUploadingGallery(false);
        }
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            setError("Product title is required.");
            return false;
        }
        if (!formData.category_id) {
            setError("Sub category is required.");
            return false;
        }
        if (!formData.brand_id) {
            setError("Brand is required.");
            return false;
        }
        if (formData.buying_price === "" || Number(formData.buying_price) < 0) {
            setError("Please enter a valid Buying Price.");
            return false;
        }
        if (formData.sell_price_dollar === "" || Number(formData.sell_price_dollar) < 0) {
            setError("Please enter sell price in dollar.");
            return false;
        }
        if (formData.shipping_charges_dollar === "" || Number(formData.shipping_charges_dollar) < 0) {
            setError("Please enter shipping charges in dollar.");
            return false;
        }
        if (formData.buying_price === "" || Number(formData.buying_price) < 0) {
            setError("Please enter buying price.");
            return false;
        }
        if (formData.short_buying_price === "" || Number(formData.short_buying_price) < 0) {
            setError("Please enter short buying price.");
            return false;
        }
        if (!formData.short_description.trim()) {
            setError("Short description is required.");
            return false;
        }
        if (!formData.description.trim() || formData.description === "<p><br></p>") {
            setError("Full description is required.");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!validateForm()) return;
        setSaving(true);

        try {
            const submitData = new FormData();

            // Appending all validated backend fields
            submitData.append("category_id", formData.category_id);
            submitData.append("brand_id", formData.brand_id);
            submitData.append("title", formData.title);
            submitData.append("description", formData.description);
            submitData.append("short_description", formData.short_description);
            submitData.append("product_condition", formData.product_condition);
            submitData.append("buying_price", formData.buying_price);
            submitData.append("sell_price_dollar", formData.sell_price_dollar);
            submitData.append("shipping_charges_dollar", formData.shipping_charges_dollar);
            submitData.append("product_qty", formData.product_qty);
            submitData.append("short_buying_price", formData.short_buying_price);
            submitData.append("buying_price", formData.buying_price);

            if (mainImage) {
                submitData.append("card_img", mainImage);
            }

            // Route: Route::post('new-products/{product}', [SellerProductController::class, 'updateFreshProduct'])
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/store-seller/new-products/${formData.product_id}`,
                submitData,
                {
                    headers: {
                        ...getAuthHeaders(),
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data?.success === false) {
                setError(response.data?.message || "Failed to update product.");
                return;
            }

            setSuccess(response.data?.message || "Product updated successfully!");
            setTimeout(() => navigate("/seller-products"), 1000);
        } catch (err) {
            console.error("Update product error:", err);
            if (err.response?.data?.errors) {
                const messages = Object.values(err.response.data.errors).flat().join(" ");
                setError(messages);
            } else {
                setError(err.response?.data?.message || "Failed to update product.");
            }
        } finally {
            setSaving(false);
        }
    };

    if (loadingInit) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading Categories & Brands...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Edit Product</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to="#">Home</Link></li>
                                <li className="mc-breadcrumb-item">Edit Product</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                    {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
                </Col>

                <Col xl={12}>
                    <Form onSubmit={handleSubmit}>
                        {/* Basic Product Info */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Basic Product Information</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Product Title <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="title"
                                                placeholder="e.g. Wireless Bluetooth Headphones"
                                                value={formData.title}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Parent Category Select */}
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Main Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                value={selectedParentCategory}
                                                onChange={handleParentCategoryChange}
                                                required
                                            >
                                                <option value="">Select Main Category</option>
                                                {categories.map((cat) => (
                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Child Category Select */}
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sub Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                name="category_id"
                                                value={formData.category_id}
                                                onChange={handleChange}
                                                disabled={!subCategories.length}
                                                required
                                            >
                                                <option value="">
                                                    {subCategories.length ? "Select Sub Category" : "No Sub Categories Available"}
                                                </option>
                                                {subCategories.map((sub) => (
                                                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Brand Select */}
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Brand <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                name="brand_id"
                                                value={formData.brand_id}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select Brand</option>
                                                {brands.map((b) => (
                                                    <option key={b.id} value={b.id}>{b.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Condition Select */}
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Condition <span className="text-danger">*</span></Form.Label>
                                            <Form.Select name="product_condition" value={formData.product_condition} onChange={handleChange} required>
                                                <option value="new">New</option>
                                                <option value="used">Used</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Quantity</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                name="product_qty"
                                                placeholder="e.g. 50"
                                                value={formData.product_qty}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Pricing & Cost Info */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Pricing & Cost Details</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Buying Price <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="buying_price"
                                                placeholder="e.g. 1500.00"
                                                value={formData.buying_price}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sell Price Dollar ($) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="sell_price_dollar"
                                                placeholder="e.g. 25.50"
                                                value={formData.sell_price_dollar}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Shipping Charges ($) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="shipping_charges_dollar"
                                                placeholder="e.g. 5.00"
                                                value={formData.shipping_charges_dollar}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Buying Price <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                name="buying_price"
                                                placeholder="e.g. 1000"
                                                value={formData.buying_price}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Short Buying Price <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                name="short_buying_price"
                                                placeholder="e.g. 950"
                                                value={formData.short_buying_price}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Descriptions */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Product Overview</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col xs={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Short Description <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="short_description"
                                                placeholder="Brief overview of the product..."
                                                value={formData.short_description}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Full Description <span className="text-danger">*</span></Form.Label>
                                            <ReactQuill
                                                theme="snow"
                                                placeholder="Enter full specification and details..."
                                                value={formData.description}
                                                onChange={handleQuillChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Main Card Image */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Main Card Image</h5></Card.Header>
                            <Card.Body>
                                <Row className="align-items-center">
                                    <Col md={3}>
                                        <div style={{ width: "100%", height: "150px", border: "1px dashed #ccc", borderRadius: "8px", overflow: "hidden" }}>
                                            {mainImagePreview ? (
                                                <img src={mainImagePreview} alt="Main Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                            ) : (
                                                <div className="d-flex h-100 align-items-center justify-content-center text-muted">No Card Image</div>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md={9}>
                                        <Form.Control type="file" accept="image/jpg,image/jpeg,image/png,image/webp" onChange={handleMainImageChange} />
                                        <Form.Text className="text-muted">Leave blank to keep existing image</Form.Text>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Gallery Images */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Gallery Images</h5>
                                {uploadingGallery && <Spinner size="sm" animation="border" variant="primary" />}
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    {imagesList.map((imgItem) => (
                                        <Col md={3} sm={4} key={imgItem.id}>
                                            <div className="position-relative border rounded overflow-hidden" style={{ height: "140px" }}>
                                                <img
                                                    src={`${process.env.REACT_APP_IMG_URL}/${imgItem.image}`}
                                                    alt="Gallery Item"
                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                />
                                                <label
                                                    className="position-absolute bottom-0 end-0 m-1 bg-white p-1 rounded-circle shadow"
                                                    style={{ cursor: "pointer" }}
                                                    title="Replace image"
                                                >
                                                    <FiRefreshCw size={14} className="text-primary" />
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/jpg,image/jpeg,image/png,image/webp"
                                                        onChange={(e) => handleSingleImageReplace(imgItem.id, e.target.files?.[0])}
                                                    />
                                                </label>
                                            </div>
                                        </Col>
                                    ))}

                                    <Col md={3} sm={4}>
                                        <label
                                            className="d-flex flex-column align-items-center justify-content-center border border-primary border-dashed rounded text-primary"
                                            style={{ height: "140px", cursor: "pointer", backgroundColor: "#f8f9fa" }}
                                        >
                                            <FiPlus size={28} />
                                            <span className="fw-bold mt-1">Add Multi Images</span>
                                            <input type="file" hidden multiple accept="image/jpg,image/jpeg,image/png,image/webp" onChange={handleMultiImageUpload} />
                                        </label>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end gap-2 mb-5">
                            <Button variant="secondary" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
                            <Button type="submit" variant="primary" disabled={saving}>
                                {saving ? <Spinner size="sm" animation="border" /> : "Update Product"}
                            </Button>
                        </div>
                    </Form>
                </Col>
            </Row>
        </PageLayout>
    );
}